import clsx from "clsx";
import { useState } from "react";

import { useManageSubscription } from "features/subscriptions/context/ManageSubscriptionContext";
import { getRentalAccountsForSub } from "features/subscriptions/helpers/rentalSubscriptions";

import { Button } from "common/components/ui/Button";
import Checkbox from "common/components/ui/Checkbox";
import {
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import ProfileImage from "common/components/ui/ProfileImage";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

interface AccountsToCancelProps {
  goBack: () => void;
  accountsToCancel: string[];
  setAccountsToCancel: (accounts: string[]) => void;
  seatsToCancel: number;
  updateSubscriptionButton: JSX.Element;
}

export default function AccountsToCancel({
  goBack,
  accountsToCancel,
  setAccountsToCancel,
  seatsToCancel,
  updateSubscriptionButton,
}: AccountsToCancelProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { accounts } = useSelectedWorkspaceContext();
  const { rentalOrderSubscription } = useManageSubscription();

  const [isShowingConfirmation, setIsShowingConfirmation] = useState(false);

  //rental orders cancel the avatars assigned to that order, everything else
  //cancels outreach licences
  const isRental = !!rentalOrderSubscription;

  const selectableAccounts = rentalOrderSubscription
    ? getRentalAccountsForSub(accounts, rentalOrderSubscription.id)
    : accounts.filter(({ license, type }) => type === "user" && !!license);

  const isLimitReached = accountsToCancel.length >= seatsToCancel;

  const seatLabel = isRental ? "avatar" : "seat";
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  return (
    <>
      <Header
        title={
          isShowingConfirmation
            ? `Are you sure you want to remove ${accountsToCancel.length} ${seatLabel}${accountsToCancel.length > 1 ? "s" : ""}?`
            : isRental
              ? "Remove avatars"
              : "Licence cancellation"
        }
        subtitle={
          isShowingConfirmation
            ? isRental
              ? "Avatars will remain available until your next billing date. After that, they will be removed and you will not be billed for them"
              : "Seats will remain available for use until your next billing date. After that, they will be unassigned and you will not be billed for them"
            : isRental
              ? "Select which Avatars you want to remove when your subscription downgrades."
              : "You will not have enough outreach seats for your currently active accounts. Choose which accounts will have their seats removed"
        }
      />

      <Body>
        {!isShowingConfirmation && (
          <>
            <p className="px-2 text-black-500">
              Selected:{" "}
              <span className="text-sm-bold text-black-950">
                {accountsToCancel.length}/{seatsToCancel}
              </span>
            </p>

            <section className="flex flex-col gap-y-3 py-3 px-2 overflow-y-auto scrollbar-thin pb-4 lg:max-h-94.5">
              {selectableAccounts.map(
                ({
                  id,
                  full_name: fullName,
                  picture_url: pictureUrl,
                  occupation,
                }) => {
                  const isSelected = !!accountsToCancel.find(
                    (accountId) => accountId === id,
                  );

                  return (
                    <button
                      type="button"
                      key={id}
                      className={clsx(
                        "flex items-center gap-x-2 rounded-3xl border  transition-[background,border] ease-in-out p-3 hover:bg-black-100",
                        isSelected
                          ? "border-purple-500 bg-purple-50"
                          : "border-black-200",
                      )}
                      onClick={() => {
                        if (isSelected) {
                          setAccountsToCancel(
                            accountsToCancel.filter(
                              (accountId) => accountId !== id,
                            ),
                          );
                        } else if (!isRental || !isLimitReached) {
                          //rental orders require an exact selection, so block
                          //selecting more accounts than needed
                          setAccountsToCancel([...accountsToCancel, id]);
                        }
                      }}
                    >
                      <ProfileImage src={pictureUrl} />
                      <div className="flex flex-1 flex-col gap-y-0.5">
                        <h5 className="line-clamp-1 text-left text-sm-bold group-disabled:text-black-400">
                          {fullName}
                        </h5>
                        <span className="line-clamp-1  text-left text-xs-medium text-black-500 group-disabled:text-black-400">
                          {occupation}
                        </span>
                      </div>
                      <Checkbox checked={isSelected} isStyleOnly />
                    </button>
                  );
                },
              )}
            </section>
          </>
        )}
      </Body>

      <Footer className="justify-end">
        <Button
          variant="secondary-black"
          onClick={
            isShowingConfirmation
              ? () => setIsShowingConfirmation(false)
              : goBack
          }
        >
          {isRental && !isShowingConfirmation ? "Cancel" : "Back"}
        </Button>
        {isShowingConfirmation ? (
          updateSubscriptionButton
        ) : (
          <Button
            onClick={() => setIsShowingConfirmation(true)}
            disabled={!isLimitReached}
            variant="default-black"
          >
            Next
          </Button>
        )}
      </Footer>
    </>
  );
}
